import * as Headless from '@headlessui/react';
import NextLink, { type LinkProps } from 'next/link';
import React, { forwardRef } from 'react';

const styles = {
  colors: {
    light: 'text-white hover:text-gray-100 dark:hover:text-gray-200',
    default:
      'text-[var(--color-primary-blurple)] hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300',
  },
};

const sizeStyles = {
  tiny: { fontSize: '0.75rem', lineHeight: '1rem' },
  small: { fontSize: '0.875rem', lineHeight: '1.25rem' },
  base: { fontSize: '1rem', lineHeight: '1.5rem' },
  large: { fontSize: '1.125rem', lineHeight: '1.75rem' },
};

type LinkColor = keyof typeof styles.colors;
type LinkComponentProps = Partial<LinkProps> &
  React.ComponentPropsWithoutRef<'a'> &
  React.ComponentPropsWithoutRef<'button'> & {
    headless?: boolean;
    color?: LinkColor;
    size?: 'tiny' | 'small' | 'base' | 'large' | 'inherit';
    underline?: boolean;
  };

export const Link = forwardRef<HTMLAnchorElement | HTMLButtonElement, LinkComponentProps>((props, ref) => {
  const { className, headless = false, href, color = 'default', size = 'base', underline = true, ...rest } = props;

  const linkClassName = `font-semibold whitespace-nowrap ${underline ? '!underline' : 'no-underline'} underline-offset-2 text-[var(--color-primary-blurple)] hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 ${className || ''}`;
  const linkStyle = { fontFamily: 'inherit', fontSize: 'inherit' };

  // If no href, render as button
  if (!href) {
    const buttonStyle =
      size === 'inherit'
        ? { ...linkStyle, cursor: 'pointer' }
        : { ...linkStyle, cursor: 'pointer', ...sizeStyles[size] };

    return (
      <Headless.DataInteractive>
        <button
          type="button"
          {...(rest as React.ComponentPropsWithoutRef<'button'>)}
          ref={ref as React.ForwardedRef<HTMLButtonElement>}
          className={linkClassName}
          style={buttonStyle}
        />
      </Headless.DataInteractive>
    );
  }

  if (headless) {
    return (
      <Headless.DataInteractive>
        <NextLink {...rest} href={href} ref={ref as React.ForwardedRef<HTMLAnchorElement>} className={className} />
      </Headless.DataInteractive>
    );
  }

  const colorClasses = styles.colors[color];

  return (
    <Headless.DataInteractive>
      <NextLink
        {...rest}
        href={href}
        ref={ref as React.ForwardedRef<HTMLAnchorElement>}
        className={`${linkClassName} ${colorClasses} ${className || ''}`}
        style={linkStyle}
      />
    </Headless.DataInteractive>
  );
});
